import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Sparkles, RefreshCw, ArrowRight, XCircle, RotateCcw, Download } from "lucide-react";
import { toast } from "sonner";
import { QidChipList, EvidenceRefsHelper, formatQidRefs } from "@/lib/qidFormat";

/**
 * QuickSnapshot — Preliminary Quick Repath Snapshot result page.
 *
 * Loads any existing snapshot; provides a "Generate" button when none is
 * present. All conclusions can be rejected/restored by the participant.
 * MANDATORY language: "This is preliminary guidance..." is rendered
 * as the first visible banner. Complete Assessment hand-off is present.
 */
export default function QuickSnapshot() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshotRow, setSnapshotRow] = useState(null); // {id, snapshot, usage, rejected_conclusion_ids}
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lanes, setLanes] = useState(null);
  const navigate = useNavigate();

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: snap }, { data: state }, { data: lanesData }] = await Promise.all([
        api.get("/assessment/quick-snapshot/current"),
        api.get("/assessment/state"),
        api.get("/assessment/lanes"),
      ]);
      setSnapshotRow(snap?.snapshot ? snap : null);
      setProgress(state.progress);
      setLanes(lanesData);
    } catch (err) {
      setError(extractApiError(err, "Couldn't load your snapshot."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCurrent(); }, [loadCurrent]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      // Fetch the CURRENT ai_processing policy version live so we never
      // post a stale hardcoded value the backend correctly rejects
      // (Fix cycle 2026-09-24 — Mark's consent-loop bug). The
      // participant reaches this page only after AssessmentConsent
      // already recorded their consent to the current version; here
      // we just re-read it so submit-quick + generate use the same
      // canonical string.
      const { data: pol } = await api.get("/policies/ai_processing");
      const aiVersion = pol?.version;
      if (!aiVersion) {
        throw new Error("Could not read current AI-processing policy version.");
      }
      // The assessment/submit-quick call is idempotent — safe to invoke
      // even if this is a regenerate.
      await api.post("/assessment/submit-quick", { ai_processing_version: aiVersion });
      const { data } = await api.post("/assessment/quick-snapshot/generate", {
        ai_processing_version: aiVersion,
      });
      setSnapshotRow(data);
      toast.success("Your Quick Repath Snapshot is ready.");
    } catch (err) {
      const detail = extractApiError(err, "Snapshot couldn't be generated right now.");
      setError(detail);
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const rejectConclusion = async (conclusion_id) => {
    if (!snapshotRow) return;
    try {
      const { data } = await api.post("/assessment/quick-snapshot/action", {
        snapshot_id: snapshotRow.id,
        conclusion_id,
        action: "reject",
      });
      setSnapshotRow({ ...snapshotRow, rejected_conclusion_ids: data.rejected_conclusion_ids });
    } catch (err) {
      toast.error(extractApiError(err, "Couldn't record your rejection."));
    }
  };

  const restoreConclusion = async (conclusion_id) => {
    if (!snapshotRow) return;
    try {
      const { data } = await api.post("/assessment/quick-snapshot/action", {
        snapshot_id: snapshotRow.id,
        conclusion_id,
        action: "restore",
      });
      setSnapshotRow({ ...snapshotRow, rejected_conclusion_ids: data.rejected_conclusion_ids });
    } catch (err) {
      toast.error(extractApiError(err, "Couldn't restore this conclusion."));
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadSnapshotPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get("/pdf/quick-snapshot", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cra3d_quick_repath_snapshot.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extractApiError(err, "Couldn't download the snapshot PDF."));
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div data-testid="quick-snapshot-loading" className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading your snapshot…
        </div>
      </AppShell>
    );
  }

  const rejected = new Set(snapshotRow?.rejected_conclusion_ids || []);

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-wider text-fg-secondary">Preliminary Quick Repath Snapshot</p>
        <h1
          data-testid="quick-snapshot-title"
          className="mt-1 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
        >
          Your early sense of directions.
        </h1>

        <div
          data-testid="quick-snapshot-preliminary-notice"
          className="mt-6 rounded-md bg-progress-teal-bg/30 border border-progress-teal/40 p-4"
        >
          <p className="text-sm text-fg-primary">
            <strong>This is preliminary guidance based on the shorter Quick
            Repath assessment.</strong> The Complete Career Repath Assessment
            provides stronger evidence and more detailed direction choices.
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mt-4" data-testid="quick-snapshot-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!snapshotRow && (
          <div className="mt-6 rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="quick-snapshot-empty">
            <p className="text-sm text-fg-secondary">
              You haven't generated a snapshot yet. Make sure your Quick Repath
              answers are saved, then generate below.
            </p>
            <Button
              onClick={generate}
              disabled={generating}
              data-testid="quick-snapshot-generate-btn"
              className="mt-4 bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-50"
            >
              {generating
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                : <Sparkles className="h-4 w-4 mr-2" aria-hidden="true" />}
              {generating ? "Generating…" : "Generate my snapshot"}
            </Button>
            <div className="mt-4">
              <Link to="/assessment?lane=quick" className="text-xs text-fg-secondary underline hover:text-fg-primary" data-testid="quick-snapshot-back-to-questions">
                ← Back to the Quick Repath questions
              </Link>
            </div>
          </div>
        )}

        {snapshotRow && (
          <SnapshotBody
            snapshotRow={snapshotRow}
            rejected={rejected}
            onReject={rejectConclusion}
            onRestore={restoreConclusion}
            onRegenerate={generate}
            regenerating={generating}
          />
        )}

        <div className="mt-10 rounded-lg border border-accent-warm/30 bg-accent-warm/5 p-6" data-testid="continue-to-complete-panel">
          <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">Ready for stronger evidence?</p>
          <h2 className="text-xl font-heading text-fg-primary">What the Complete Career Repath Assessment unlocks</h2>
          <p className="mt-2 text-sm text-fg-secondary leading-relaxed">
            Your Quick Repath snapshot stays here — it will be marked as an
            earlier view, not deleted. The Complete Career Repath Assessment
            picks up your matching answers automatically. New questions that
            only appear in the Complete Career Repath Assessment still need
            answering.
          </p>
          {lanes && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="complete-lane-unlocks">
              <UnlockRow testid="unlocks-profile" title="Full nine-part profile" body="Evidence-linked strengths, interests, work-style, learning preferences, digital confidence band, constraints, and honest uncertainties — every conclusion drafted by AI is correctable or rejectable by you." />
              <UnlockRow testid="unlocks-directions" title="Three detailed Lanes of Direction" body="One per category (closest transition, AI-supported growth, independent path) when your evidence supports it — with skill-gap analysis and honest tradeoffs on each." />
              <UnlockRow testid="unlocks-skillgap" title="Skill-gap analysis per direction" body="What you already bring, what the direction requires, and which gaps CRA3D can help you close." />
              <UnlockRow testid="unlocks-pathway" title="Evidence-based pathway matching" body="Once you confirm a direction, CRA3D maps it to the closest learning pathway — you're always free to choose any." />
            </div>
          )}
          {lanes && (
            <div className="mt-4 rounded-md border border-border-subtle bg-bg-surface p-3 text-xs" data-testid="complete-lane-remaining">
              <p className="text-fg-primary">
                <span className="font-medium">{Math.max(0, (lanes.complete?.question_count || 0) - (lanes.complete?.answered_carryover || 0))}</span> questions still to answer
                <span className="text-fg-secondary">
                  {" "}— you've already answered <span className="text-fg-primary">{lanes.complete?.answered_carryover || 0}</span> of {lanes.complete?.question_count || 0} in the Complete Career Repath Assessment through the Quick Repath.
                </span>
              </p>
              <p className="mt-1 text-fg-muted italic">
                No timers, no scores, no shame. You can stop and resume any time — matching answers carry over losslessly.
              </p>
            </div>
          )}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={async () => {
                try {
                  await api.post("/assessment/lane/set", { lane: "complete" });
                  navigate("/assessment?lane=complete");
                } catch (err) {
                  const status = err?.response?.status;
                  if (status === 402) {
                    // Paid-gate: send them to the pricing page in context.
                    navigate("/plans?gated_from=complete-assessment");
                    return;
                  }
                  toast.error(extractApiError(err, "Couldn't switch to the Complete Career Repath Assessment."));
                }
              }}
              data-testid="continue-to-complete-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              {lanes?.complete?.locked
                ? `Unlock Complete Assessment — ${lanes?.complete?.price_display || "$24.99"}`
                : "Continue to Complete Career Repath Assessment"}
              <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
            </Button>
            <Button
              onClick={downloadSnapshotPdf}
              disabled={downloadingPdf || !snapshotRow}
              data-testid="quick-snapshot-download-pdf-btn"
              variant="outline"
              className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              {downloadingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4 mr-2" aria-hidden="true" />
              )}
              Download snapshot PDF
            </Button>
            <Button
              variant="ghost"
              onClick={loadCurrent}
              data-testid="quick-snapshot-reload"
              className="text-fg-secondary hover:text-fg-primary"
            >
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" /> Reload snapshot
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function UnlockRow({ testid, title, body }) {
  return (
    <div data-testid={testid} className="rounded-md border border-border-subtle bg-bg-surface p-3">
      <p className="text-sm text-fg-primary font-medium">{title}</p>
      <p className="mt-1 text-xs text-fg-secondary">{body}</p>
    </div>
  );
}

function SnapshotBody({ snapshotRow, rejected, onReject, onRestore, onRegenerate, regenerating }) {  const snap = snapshotRow.snapshot;
  return (
    <div className="mt-6 space-y-6">
      {/* What you told us */}
      <section className="rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="snapshot-what-you-told-us">
        <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">What you told us</p>
        <p className="text-base text-fg-primary leading-relaxed">{formatQidRefs(snap.what_you_told_us)}</p>
      </section>

      {/* Helper for evidence refs — sits above the first section that
          cites specific answers (preliminary strengths). */}
      <EvidenceRefsHelper testid="snapshot-evidence-refs-helper" />

      {/* Preliminary interests */}
      {(snap.preliminary_interests || []).length > 0 && (
        <section className="rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="snapshot-preliminary-interests">
          <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">Preliminary interests</p>
          <ul className="space-y-2">
            {snap.preliminary_interests.map((i, idx) => (
              <li key={idx} className="text-sm text-fg-primary" data-testid={`snapshot-interest-${idx}`}>· {formatQidRefs(i)}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Preliminary strengths */}
      <section className="rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="snapshot-preliminary-strengths">
        <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">Preliminary strengths</p>
        <ul className="space-y-3">
          {(snap.preliminary_strengths || []).map((s) => {
            const isRejected = rejected.has(s.id);
            const hasRefs = (s.supporting_answers || []).some(
              (q) => /^s\d{1,3}_q\d{1,3}$/i.test(String(q || "").trim())
            );
            return (
              <li key={s.id} className={`rounded-md border p-3 ${isRejected ? "opacity-50 line-through" : "border-border-subtle"}`} data-testid={`snapshot-strength-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-fg-primary font-medium">{formatQidRefs(s.strength)}</p>
                    <p className="mt-1 text-xs text-fg-secondary">
                      Evidence: <span className="uppercase tracking-wider">{s.evidence_type}</span> · Confidence: {s.plain_language_confidence}
                    </p>
                    <div className="mt-2 flex flex-wrap items-baseline gap-2">
                      <span className="text-xs text-fg-muted">Supporting answers:</span>
                      {hasRefs ? (
                        <QidChipList
                          qids={s.supporting_answers}
                          testidPrefix={`snapshot-strength-${s.id}-ref`}
                        />
                      ) : (
                        <span className="text-xs text-fg-muted">—</span>
                      )}
                    </div>
                  </div>
                  {isRejected ? (
                    <Button size="sm" variant="ghost" onClick={() => onRestore(s.id)} data-testid={`snapshot-strength-${s.id}-restore`} className="text-fg-secondary hover:text-fg-primary">
                      <RotateCcw className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Restore
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => onReject(s.id)} data-testid={`snapshot-strength-${s.id}-reject`} className="text-fg-secondary hover:text-destructive">
                      <XCircle className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Reject
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Direction areas */}
      <section className="rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="snapshot-direction-areas">
        <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">Possible work-direction areas</p>
        <p className="text-xs text-fg-muted mb-3">Lighter than full directions — a first sense of where your answers point.</p>
        <ul className="space-y-3">
          {(snap.possible_direction_areas || []).map((d) => {
            const isRejected = rejected.has(d.id);
            const hasRefs = (d.supporting_answers || []).some(
              (q) => /^s\d{1,3}_q\d{1,3}$/i.test(String(q || "").trim())
            );
            return (
              <li key={d.id} className={`rounded-md border p-3 ${isRejected ? "opacity-50 line-through" : "border-border-subtle"}`} data-testid={`snapshot-direction-${d.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-fg-primary font-medium">{formatQidRefs(d.area)}</p>
                    <p className="mt-1 text-sm text-fg-secondary">{formatQidRefs(d.why)}</p>
                    <div className="mt-2 flex flex-wrap items-baseline gap-2">
                      <span className="text-xs text-fg-muted">Supporting answers:</span>
                      {hasRefs ? (
                        <QidChipList
                          qids={d.supporting_answers}
                          testidPrefix={`snapshot-direction-${d.id}-ref`}
                        />
                      ) : (
                        <span className="text-xs text-fg-muted">—</span>
                      )}
                    </div>
                  </div>
                  {isRejected ? (
                    <Button size="sm" variant="ghost" onClick={() => onRestore(d.id)} data-testid={`snapshot-direction-${d.id}-restore`} className="text-fg-secondary hover:text-fg-primary">
                      <RotateCcw className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Restore
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => onReject(d.id)} data-testid={`snapshot-direction-${d.id}-reject`} className="text-fg-secondary hover:text-destructive">
                      <XCircle className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Reject
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Constraints */}
      {(snap.important_constraints || []).length > 0 && (
        <section className="rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="snapshot-constraints">
          <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">Important constraints</p>
          <ul className="space-y-2">
            {snap.important_constraints.map((c, idx) => (
              <li key={idx} className="text-sm text-fg-primary" data-testid={`snapshot-constraint-${idx}`}>· {c}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Missing information */}
      {(snap.missing_information || []).length > 0 && (
        <section className="rounded-lg border border-border-subtle bg-bg-surface p-6" data-testid="snapshot-missing">
          <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">What would strengthen this picture</p>
          <ul className="space-y-2">
            {snap.missing_information.map((m, idx) => (
              <li key={idx} className="text-sm text-fg-primary" data-testid={`snapshot-missing-${idx}`}>· {m}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Next step */}
      {snap.one_practical_next_step && (
        <section className="rounded-lg border border-accent-warm/30 bg-accent-warm/5 p-6" data-testid="snapshot-next-step">
          <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">One practical next step</p>
          <p className="text-base text-fg-primary">{snap.one_practical_next_step}</p>
        </section>
      )}

      {/* Regenerate control */}
      <div className="text-right">
        <Button
          size="sm"
          variant="ghost"
          onClick={onRegenerate}
          disabled={regenerating}
          data-testid="quick-snapshot-regenerate-btn"
          className="text-fg-secondary hover:text-fg-primary"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${regenerating ? "animate-spin" : ""}`} aria-hidden="true" />
          {regenerating ? "Regenerating…" : "Regenerate snapshot"}
        </Button>
      </div>
    </div>
  );
}
